import {
  AlignType,
  ETextStyleVariant,
  GeneralManageVehiclesOutlineIcon,
  GeneralMapPinOutlineIcon,
  Selector,
} from '@outdoorsyco/bonfire';
import React from 'react';
import { useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';

import { ESearchFilters } from '@/constants/searchFilters';
import { applySearchFilter } from '@/redux/modules/search';
import { getQueryParams } from '@/redux/selectors/queryParams';
import { EDeliveryOption } from '@/services/types/core/delivery.types';
import { getPreservedFilters } from '@/utility/modeSwitching';
import { isDeliveryModeFromParams } from '@/utility/queryParams';

interface IModeSwitcherContentProps {
  open?: boolean;
  onModeSelect?: () => void;
}

const ModeSwitcherContent: React.FC<IModeSwitcherContentProps> = ({ open, onModeSelect }) => {
  const intl = useIntl();
  const dispatch = useDispatch();
  const queryParams = useSelector(getQueryParams);

  const isDeliveryMode = isDeliveryModeFromParams(queryParams);

  const handleModeSelect = (isSwitchingToDelivery: boolean) => {
    // Get current query params and filter to only preserve Location, Dates, and Guests
    const preservedParams = getPreservedFilters(queryParams);

    if (isSwitchingToDelivery) {
      // Apply delivery=true and deliveryStationary=stationary along with preserved filters
      dispatch(
        applySearchFilter(
          {
            ...preservedParams,
            [ESearchFilters.DELIVERY]: 'true',
            [ESearchFilters.DELIVERY_STATIONARY]: EDeliveryOption.STATIONARY,
          },
          false,
          false,
          false,
          undefined,
          true,
        ), // ignoreExistingQueryParams = true to clear all other filters
      );
    } else {
      // Apply rentals mode (remove delivery filters) along with preserved filters
      dispatch(
        applySearchFilter(
          {
            ...preservedParams,
            [ESearchFilters.DELIVERY]: undefined,
            [ESearchFilters.DELIVERY_STATIONARY]: undefined,
          },
          false,
          false,
          false,
          undefined,
          true,
        ), // ignoreExistingQueryParams = true to clear all other filters
      );
    }

    if (onModeSelect) {
      onModeSelect();
    }
  };

  if (!open) {
    return null;
  }

  return (
    <div className="flex gap-3">
      {/* Rentals mode */}
      <Selector
        alignVariant={AlignType.VerticalLeft}
        ariaLabel="Rentals mode"
        className="flex-1"
        description={intl.formatMessage({
          defaultMessage: 'RVs to drive or tow',
          id: 'Jy3eQB',
          description: 'Rentals mode description',
        })}
        icon={GeneralManageVehiclesOutlineIcon}
        isSelected={!isDeliveryMode}
        label={intl.formatMessage({
          defaultMessage: 'Rentals',
          id: 'WOphE2',
          description: 'Rentals mode option',
        })}
        labelVariant={ETextStyleVariant.MediumBold}
        name="rentals-mode"
        onChange={() => handleModeSelect(false)}
        value="rentals"
        withBorder
      />

      {/* Delivery Mode */}
      <Selector
        alignVariant={AlignType.VerticalLeft}
        ariaLabel="Delivery mode"
        className="flex-1"
        description={intl.formatMessage({
          defaultMessage: 'Ready-to-stay RVs',
          id: 'oGDsjD',
          description: 'Delivery mode description',
        })}
        icon={GeneralMapPinOutlineIcon}
        isSelected={isDeliveryMode}
        label={intl.formatMessage({
          defaultMessage: 'Delivery',
          id: 'oxGlxs',
          description: 'Delivery mode option',
        })}
        labelVariant={ETextStyleVariant.MediumBold}
        name="delivery-mode"
        onChange={() => handleModeSelect(true)}
        value="delivery"
        withBorder
      />
    </div>
  );
};

export default ModeSwitcherContent;
